// EBML magic — every WebM file starts with these 4 bytes.
const EBML_MAGIC = new Uint8Array([0x1a, 0x45, 0xdf, 0xa3]);
const SCAN_BYTES = 64 * 1024;
// Defense-in-depth cap on "recoverable" prefix length. The observed EN-719 corruption
// prepends ~44 bytes of stale Matroska/Opus bytes. A real valid WebM cannot land here
// at all (the spec requires EBML magic at offset 0; we early-return "valid" before any
// payload scan). But if a 4-byte sequence happens to coincidentally match the magic
// deep in non-WebM garbage, capping the strip distance prevents us from slicing
// thousands of bytes off a file we don't actually understand.
const MAX_RECOVERABLE_PREFIX_BYTES = 4096;

// How many bytes a caller needs to read off the head of a blob to fully exercise
// validateWebmHeader without loading the whole file. Exported so callers can `file.slice()`
// instead of `file.arrayBuffer()` on multi-hundred-MB recordings (avoids OOM on tablets).
export const WEBM_HEADER_SCAN_BYTES = SCAN_BYTES;

// Three-way classification of a candidate WebM buffer's header. `prefixHex` (first up to
// 64 bytes as lowercase hex) is always present so Sentry events for any outcome can be
// inspected with the same envelope.
export type WebmHeaderResult =
    | { kind: "valid"; prefixHex: string }
    | { kind: "recoverable"; prefixBytes: number; prefixHex: string }
    | { kind: "invalid"; prefixHex: string };

const indexOfMagic = (bytes: Uint8Array, limit: number): number => {
    // Last position where the full magic fits within both the buffer and the scan limit.
    const stop = Math.min(bytes.length - EBML_MAGIC.length, limit - EBML_MAGIC.length);
    outer: for (let i = 0; i <= stop; i++) {
        for (let j = 0; j < EBML_MAGIC.length; j++) {
            if (bytes[i + j] !== EBML_MAGIC[j]) continue outer;
        }
        return i;
    }
    return -1;
};

const toHex = (bytes: Uint8Array, n: number): string => {
    const len = Math.min(n, bytes.length);
    let out = "";
    for (let i = 0; i < len; i++) {
        const b = bytes[i].toString(16).padStart(2, "0");
        out += b;
    }
    return out;
};

// Quick plausibility check: after the EBML magic, the next byte is the start of a
// VINT-encoded length for the EBML Header element. Per the EBML spec (RFC 9559) any
// non-zero byte is a syntactically valid VINT start — the leading-zero count encodes
// the width (1xxxxxxx = 1 byte, 01xxxxxx = 2 bytes, etc.). 0x00 is the only
// structurally impossible value. This is a weak filter on top of the 4-byte magic
// match, but it cheaply rejects all-zero garbage that happened to contain a magic
// collision in the middle.
const looksLikeEbmlHeaderAfterMagic = (bytes: Uint8Array, magicOffset: number): boolean => {
    const vintIndex = magicOffset + EBML_MAGIC.length;
    if (vintIndex >= bytes.length) return false;
    return bytes[vintIndex] !== 0x00;
};

// Classify the first 64 KiB of a candidate WebM buffer:
//   "valid"       — starts with EBML magic; normal case. By spec a real WebM file MUST
//                   begin with the magic at offset 0, so this short-circuits before any
//                   payload scan — a valid WebM whose audio data happens to contain the
//                   4-byte magic later in the file cannot reach the "recoverable" branch.
//   "recoverable" — magic found at offset 0 < N ≤ MAX_RECOVERABLE_PREFIX_BYTES, AND the
//                   byte after the magic is a plausible VINT start. EN-719 corruption
//                   prepends ~44 bytes; the cap prevents silently slicing a large chunk
//                   off a file that just coincidentally matched the magic.
//   "invalid"     — no magic, magic past the cap, or magic not followed by a plausible
//                   EBML header. The caller surfaces this as a hard error.
export const validateWebmHeader = (bytes: Uint8Array): WebmHeaderResult => {
    const prefixHex = toHex(bytes, Math.min(64, bytes.length));
    const offset = indexOfMagic(bytes, SCAN_BYTES);
    if (offset === 0) return { kind: "valid", prefixHex };
    if (offset < 0) return { kind: "invalid", prefixHex };
    if (offset > MAX_RECOVERABLE_PREFIX_BYTES) return { kind: "invalid", prefixHex };
    if (!looksLikeEbmlHeaderAfterMagic(bytes, offset)) return { kind: "invalid", prefixHex };
    return { kind: "recoverable", prefixBytes: offset, prefixHex };
};

// First N bytes (default 4) as a lowercase hex string. Used for tagging recordings in
// Sentry breadcrumbs / events.
export const firstBytesHex = (bytes: Uint8Array, n = 4): string => toHex(bytes, n);
